import { useEffect, useRef, useState } from "react"
import Icon from "./Icon"
import { formatMoney } from "../utils/format"
import { describeBellRow } from "../pages/frp/tenancyInvitationDisplay"

const describeApprovalRequest = (request) => {
  const count = Number(request?.waitingCount || 0)
  const waiting = count > 0
    ? `${count} bill${count === 1 ? "" : "s"} waiting · ${formatMoney(request.waitingAmount)}. `
    : ""
  return `${waiting}Only approve companies you know.`
}

// Dashboard alerts used to sit as full-width coloured banners that pushed the
// wallet and modules far down the page. They live behind this bell instead:
// the count is always visible, the detail is one tap away.
function NotificationRow({ icon, tone, title, subtitle, actionLabel, onAction }) {
  return (
    <div className="flex items-start gap-2.5 px-3 py-2.5">
      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tone}`}>
        <Icon name={icon} size="0.8rem" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold leading-tight text-slate-900">{title}</p>
        <p className="mt-0.5 text-[11px] text-slate-500">{subtitle}</p>
      </div>
      <button
        type="button"
        onClick={onAction}
        className="shrink-0 rounded-lg bg-slate-900 px-2.5 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-slate-700"
      >
        {actionLabel}
      </button>
    </div>
  )
}

function DashboardNotifications({
  inviteCount = 0,
  welfareGroups = [],
  approvalRequests = [],
  tenancyInvitations = [],
  loading = false,
  onOpenInvites,
  onOpenGroup,
  onOpenApprovals,
  onOpenTenancyInvitations,
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false)
    }
    const onEscape = (event) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onClickOutside)
    document.addEventListener("keydown", onEscape)
    return () => {
      document.removeEventListener("mousedown", onClickOutside)
      document.removeEventListener("keydown", onEscape)
    }
  }, [open])

  const welfareCount = welfareGroups.reduce((total, row) => total + Number(row?.pendingCount || 0), 0)
  const total = Number(inviteCount || 0) + welfareCount + approvalRequests.length + tenancyInvitations.length
  const hasAny = total > 0

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={hasAny ? `Notifications, ${total} waiting` : "Notifications"}
        aria-expanded={open}
        className={`flex items-center rounded-lg px-2 py-2 transition-all ${
          open ? "bg-gray-100 text-gray-800" : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
        }`}
      >
        {/* Badge anchors to this inner box, not the button's own padded edge,
            so it sits tight against the bell instead of floating near the
            button's corner. */}
        <span className="relative flex h-5 w-5 items-center justify-center">
          <Icon name="bell" size="1.2rem" />
          {hasAny && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white">
              {total > 99 ? "99+" : total}
            </span>
          )}
        </span>
      </button>

      {open && (
        // Pinned to the viewport, not to the bell's own position: the bell
        // sits left of the avatar and menu icons, so anchoring the panel to
        // the bell itself pushed its left edge off-screen on a phone and cut
        // off the header text and the group name.
        <div className="fixed left-3 right-3 top-[4.5rem] z-30 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl sm:left-auto sm:right-6 sm:w-80">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5">
            <p className="text-xs font-black uppercase tracking-wide text-slate-500">Notifications</p>
            {hasAny && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-600">{total}</span>
            )}
          </div>

          <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
            {/* Units a manager assigned to this person: nobody moves in or pays
                until they Approve & Pay. */}
            {tenancyInvitations.map((invitation) => {
              const row = describeBellRow(invitation)
              return (
                <NotificationRow
                  key={`tenancy-${invitation._id}`}
                  icon="house-door-fill"
                  tone="bg-emerald-100 text-emerald-700"
                  title={row.title}
                  subtitle={row.subtitle}
                  actionLabel="Review"
                  onAction={() => {
                    setOpen(false)
                    onOpenTenancyInvitations?.(invitation._id)
                  }}
                />
              )
            })}

            {/* A company asking to be paid from the wallet comes first: until the
                person chooses, that company's bills stay unpaid. */}
            {approvalRequests.map((request) => (
              <NotificationRow
                key={`autopay-${request.providerId}`}
                icon="shield-exclamation"
                tone="bg-amber-100 text-amber-700"
                title={`${request.providerName || "A company"} asks for auto-pay`}
                subtitle={describeApprovalRequest(request)}
                actionLabel="Review"
                onAction={() => {
                  setOpen(false)
                  onOpenApprovals?.(request.providerId)
                }}
              />
            ))}

            {inviteCount > 0 && (
              <NotificationRow
                icon="envelope-fill"
                tone="bg-amber-100 text-amber-700"
                title={`${inviteCount} group invitation${inviteCount === 1 ? "" : "s"}`}
                subtitle="Accept or reject in Groups."
                actionLabel="View"
                onAction={() => {
                  setOpen(false)
                  onOpenInvites?.()
                }}
              />
            )}

            {welfareGroups.map((row) => (
              <NotificationRow
                key={row.groupId}
                icon="clipboard-check-fill"
                tone="bg-blue-100 text-blue-700"
                title={row.groupName || "Group"}
                subtitle={`${Number(row.pendingCount || 0)} welfare loss-report approval${
                  Number(row.pendingCount || 0) === 1 ? "" : "s"
                } waiting.`}
                actionLabel="Open"
                onAction={() => {
                  setOpen(false)
                  onOpenGroup?.(row.groupId)
                }}
              />
            ))}

            {!hasAny && (
              <p className="px-3 py-6 text-center text-xs text-slate-400">
                {loading ? "Checking for new notifications…" : "Nothing needs your attention."}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardNotifications
